import Service, { service } from '@ember/service';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';
import { FIXED_EXTRACT_DATE_LATEST } from 'lumen/utils/constants/market-insight-constants';

export default class MiRateindexService extends Service {
  @service intl;
  @service miHotels;

  /**
   * Gets the rateindexes from a certain day on a certain extract date
   * @param { day } day: day object from Ember Data store
   * @param { dayjs } currentExtractDate: the extract date represented by a dayjs object or latest string
   * @param { string } dateAggregation: daily or weekly view (default daily for backwards compatibility)
   * @returns { Array }: returns an array of rateindex object (from store)
   */
  getRateIndexesForExtractDate(
    day,
    currentExtractDate,
    dateAggregation = DATE_AGGREGATION.DAY,
  ) {
    let rateIndexData = null;

    if (day) {
      rateIndexData = day.rateIndexes;
    }

    let rateIndexDataForCurrentExtractDate = [];

    if (rateIndexData) {
      rateIndexData = rateIndexData.filter(
        (rateIndex) => rateIndex.dateAggregation === dateAggregation,
      );
      if (currentExtractDate === FIXED_EXTRACT_DATE_LATEST) {
        rateIndexDataForCurrentExtractDate = rateIndexData.filter(
          (rateIndexObject) => rateIndexObject.isLatest,
        );
      } else {
        rateIndexDataForCurrentExtractDate = rateIndexData.filter(
          (rateIndexObject) =>
            rateIndexObject.extractDate.isSame(currentExtractDate, 'day'),
        );
      }
    }

    return rateIndexDataForCurrentExtractDate;
  }

  /**
   * Helper function to filter rateindexes on a hotel. Also formats the response if not found
   * @param { Array<rateindex>} filteredRateIndexes
   * @param { int } ourHotelId
   * @param { string } dateAggregation: daily or weekly view (default daily for backwards compatibility)
   * @returns { Object }: returns an object containing the rateindex or a formatted object if unavailable
   */
  getRateIndexesForHotel(
    filteredRateIndexes,
    ourHotelId,
    dateAggregation = DATE_AGGREGATION.DAY,
  ) {
    const filteredRateIndexForCurrentSelection = filteredRateIndexes.filter(
      (rateIndexObject) =>
        rateIndexObject.ourHotelId === ourHotelId &&
        rateIndexObject.dateAggregation === dateAggregation,
    );

    if (filteredRateIndexForCurrentSelection.length) {
      return filteredRateIndexForCurrentSelection[0];
    }
    return {
      formattedRate: this.intl.t('general.not_available_short'),
      formattedRoomNameAndCancellableText: '',
    };
  }

  /**
   * Gets all rateindexes from a certain day for the most recent extract date
   * @param { day } day : day from store
   * @param { string } dateAggregation: daily or weekly view (default daily for backwards compatibility)
   * @returns { Array }: returns an array of rateindex object (from store)
   */
  getRateIndexesForLatestExtractDate(day) {
    let rateIndexDataForCurrentExtractDate = [];

    if (day) {
      const rateIndexData = [...day.rateIndexes];

      rateIndexDataForCurrentExtractDate = rateIndexData.filter(
        (rateIndexObject) => rateIndexObject.isLatest,
      );
    }

    return rateIndexDataForCurrentExtractDate;
  }

  getRateIndexForCurrentHotelAndExtractDate(
    day,
    extractDate,
    dateAggregation = DATE_AGGREGATION.DAY,
  ) {
    // Probably weird we pass date aggregation to both functions, however the functions are used separately as well
    // So we need to pass it to both
    const rateindexes = this.getRateIndexesForExtractDate(
      day,
      extractDate,
      dateAggregation,
    );

    return this.getRateIndexesForHotel(
      rateindexes,
      this.miHotels.ownOurHotelId,
      dateAggregation,
    );
  }

  getRateIndexForCurrentHotelAndLatestExtractDate(
    day,
    dateAggregation = DATE_AGGREGATION.DAY,
  ) {
    // Probably weird we pass date aggregation to both functions, however the functions are used separately as well
    // So we need to pass it to both
    const rateindexes = this.getRateIndexesForLatestExtractDate(
      day,
      dateAggregation,
    );

    return this.getRateIndexesForHotel(
      rateindexes,
      this.miHotels.ownOurHotelId,
      dateAggregation,
    );
  }
}
